import { AnalyticsConsent } from './analytics-consent.interface';

export const ANALYTICS_CONSENT_STORAGE_KEY = 'analytics_consent';

/**
 * Reads persisted consent without Angular, so the pre-bootstrap analytics
 * provider and the runtime service agree on what the person actually chose.
 *
 * Every failure resolves to `null`, which means "not answered" and therefore no
 * analytics: nothing stored, storage unreachable (Safari private mode throws on
 * access rather than returning null), or a stored value that is not the shape we
 * wrote. Anything we cannot read as an explicit yes is treated as a no.
 */
export function readStoredAnalyticsConsent(storage?: Storage): AnalyticsConsent | null {
  try {
    const raw = (storage ?? localStorage).getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    const candidate = parsed as Partial<AnalyticsConsent>;
    return typeof candidate.isEnabled === 'boolean' ? (candidate as AnalyticsConsent) : null;
  } catch {
    return null;
  }
}

/** Whether the person has actively opted in. Absent or malformed = no. */
export function hasAnalyticsConsent(storage?: Storage): boolean {
  return readStoredAnalyticsConsent(storage)?.isEnabled === true;
}

/**
 * The consent record for a yes or a no.
 *
 * The four `dataCollection` flags follow the top-level answer. They are stored
 * but never read — `#trackEvent` checks `isEnabled` alone — so offering them
 * separately in the UI would promise a granularity the service does not honour.
 */
export function analyticsConsentDecision(isEnabled: boolean): AnalyticsConsent {
  return {
    isEnabled,
    lastUpdated: new Date(),
    dataCollection: {
      navigation: isEnabled,
      actions: isEnabled,
      performance: isEnabled,
      errors: isEnabled,
    },
  };
}
